// Marca de la empresa (decisión D46): nombre, color, logo e íconos. Se lee sin sesión al abrir y queda guardada para
// arrancar sin señal con la cara de la empresa. El color se aplica en tiempo de ejecución sobre el tema de @reparto/ui,
// con su versión para el modo oscuro (datos/color.ts).
import { create } from 'zustand';
import { guardar, leer } from './almacen';
import { api, ErrorApi, mensajeDeError } from './api';
import { cssDeMarca, escalaDeColor } from './color';
import { claveLocal, SLUG } from './tienda-actual';
import type { Marca, MarcaPublica } from './tipos';

export const COLOR_POR_DEFECTO = '#13644a';
const CLAVE = claveLocal('marca');

type EstadoCarga = 'cargando' | 'lista' | 'no_existe' | 'error';

interface EstadoMarca {
  marca: MarcaPublica | null;
  estado: EstadoCarga;
  error: string | null;
}

const guardada = leer<MarcaPublica>(CLAVE);
export const useMarca = create<EstadoMarca>(() => ({
  marca: guardada,
  estado: guardada ? 'lista' : 'cargando',
  error: null,
}));

export async function cargarMarca(): Promise<void> {
  if (!useMarca.getState().marca) useMarca.setState({ estado: 'cargando', error: null });
  try {
    const m = await api<MarcaPublica>('/tienda/publico/marca', { consulta: { slug: SLUG }, sinSesion: true });
    guardar(CLAVE, m);
    useMarca.setState({ marca: m, estado: 'lista', error: null });
    aplicarMarca(m);
  } catch (e) {
    if (e instanceof ErrorApi && e.estado === 404) {
      useMarca.setState({ marca: null, estado: 'no_existe', error: null });
      return;
    }
    // Sin señal con la marca guardada: se sigue con la guardada.
    if (useMarca.getState().marca) return;
    useMarca.setState({ estado: 'error', error: mensajeDeError(e) });
  }
}

/**
 * Lo que index.html aplica antes de pintar (su script de arranque lee esta clave): así la tienda abre con su color y
 * su nombre, sin pasar por el gris neutro. Cada tienda es otro origen (su subdominio); en desarrollo, con varias en
 * localhost, la anterior se ve un instante hasta que llega la marca.
 */
export const CLAVE_ARRANQUE = 'tienda:arranque';

/** Color, título e íconos de la empresa en la página, en modo claro y oscuro. */
export function aplicarMarca(
  m: Pick<Marca, 'nombre' | 'nombre_corto' | 'color_primario' | 'color_texto' | 'icono_192_url'>,
) {
  const valido = !!escalaDeColor(m.color_primario);
  const color = valido ? m.color_primario : COLOR_POR_DEFECTO;
  const css = cssDeMarca(color, valido ? m.color_texto : '#ffffff');
  const d = document;
  let estilo = d.getElementById('marca');
  if (!estilo) {
    estilo = d.createElement('style');
    estilo.id = 'marca';
    d.head.appendChild(estilo);
  }
  if (estilo.textContent !== css) estilo.textContent = css;
  for (const meta of d.querySelectorAll<HTMLMetaElement>('meta[name=theme-color]')) meta.content = color;
  const titulo = d.querySelector<HTMLMetaElement>('meta[name=apple-mobile-web-app-title]');
  if (titulo) titulo.content = m.nombre_corto;
  const icono = m.icono_192_url;
  // "icon" y "apple-touch-icon".
  if (icono) for (const l of d.querySelectorAll<HTMLLinkElement>('link[rel$=icon]')) l.href = icono;
  d.title = m.nombre;
  guardar(CLAVE_ARRANQUE, { css, color, titulo: m.nombre });
}

if (typeof document !== 'undefined' && guardada) aplicarMarca(guardada);
